import React, { useContext, useEffect, useState } from 'react';

import { CafeIcon, CloseIcon, LandmarkIcon, LocationPinIcon, MuseumIcon, ParkIcon, SchoolIcon, ShopIcon, TubeIcon } from '$partials/icon';
import { ModalContext } from '$contexts/modal.jsx';
import { Map, MapCanvas } from '$partials/base/map.jsx';
import { Button } from '$partials/ui';
import { formatPrice, formatRentFrequency } from '$utilities/format';

const getMiles = (meters) => {
    return (meters*0.000621371192).toFixed(2);
};

const getKm = (meters) => {
    return (meters/1000).toFixed(1);
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modals.d.ts').MapModal}
 */
const MapModal = ({
    __typename,
    id,
    heading,
    propertyName,
    area,
    outwardCode,
    priceGbp,
    rentGbp,
    rentFrequency,
    partnershipRentFrequency,
    rentUsd,
    priceUsd,
    mapLatitude,
    mapLongitude,
    nearbyPointOfInterests,
}) => {
    const { handleModal } = useContext(ModalContext);
    const [ activeItem, setActiveItem ] = useState(null);
    const [ isFromList, setIsFromList ] = useState(false);
    const [ map, setMap ] = useState(null);
    const [ mapCenter, setMapCenter ] = useState({lat: mapLatitude, lng: mapLongitude});

    const isBuy = (__typename === 'PropertyPage') || (__typename === 'PartnershipPropertyPage');
    const isUsd = (__typename === 'PartnershipPropertyPage') || (__typename === 'PartnershipRentalPropertyPage');

    const price = (
        isBuy ?
            isUsd ?
                formatPrice(priceUsd, {currency: 'USD'})
                :
                formatPrice(priceGbp)
            : isUsd ?
                `${formatPrice((rentUsd), {currency: 'USD'})} ${formatRentFrequency(partnershipRentFrequency)}`
                :
                `${formatPrice(rentGbp)} ${formatRentFrequency(rentFrequency)}`
    );

    const poiTube = nearbyPointOfInterests.filter(poi => poi.type === 'TUBE');
    const poiAmenities = nearbyPointOfInterests.filter(poi => poi.type !== 'TUBE');

    const renderIcon = (type) => {
        switch (type) {
            case 'CAFE':
                return <CafeIcon className='flex-shrink-0' />;
            case 'PARK':
                return <ParkIcon className='flex-shrink-0'/>;
            case 'MUSEUM':
                return <MuseumIcon className='flex-shrink-0'/>;
            case 'SCHOOL':
                return <SchoolIcon className='flex-shrink-0'/>;
            case 'LANDMARK':
                return <LandmarkIcon className='flex-shrink-0'/>;
            case 'SHOP':
                return <ShopIcon className='flex-shrink-0'/>;
            case 'TUBE':
                return <TubeIcon className='flex-shrink-0'/>;
            default:
                return <LocationPinIcon className='flex-shrink-0' />;
        }
    };

    const locations = [{id: id, lat: mapLatitude, lng: mapLongitude, type: 'PIN'}].concat(nearbyPointOfInterests.map(interest => {
        return {
            lat: interest.coordinates.latitude,
            lng: interest.coordinates.longitude,
            type: interest.type,
            id: interest.id
        };
    }));

    const getDistance = (lat, lng) => {
        if (!map) {
            return null;
        }

        const distanceMeters = map.maps.geometry.spherical.computeDistanceBetween(new map.maps.LatLng(mapLatitude, mapLongitude), new map.maps.LatLng(lat, lng));
        return `${(getMiles(distanceMeters))} MI / ${getKm(distanceMeters)} KM`;
    };

    const renderActiveItem = () => {
        if (activeItem === id || !activeItem) {
            return (
                <>
                    <LocationPinIcon className='black flex-shrink-0'/>
                    <div className='flex flex-col pl5 black pr9'>
                        <p className='type-style-5'>
                            {price}
                        </p>
                        <h4 className='type-style-4'>
                            {heading}
                        </h4>
                        <div className='mt4'>
                            <p className='type-style-5'>
                                {propertyName}
                            </p>
                            <p className='type-style-5'>
                                {area}{' '}{outwardCode}
                            </p>
                        </div>
                    </div></>
            );
        }
        const activePOI = nearbyPointOfInterests.find(interest => interest.id === activeItem);
        return (
            <div className='flex items-start'>
                {renderIcon(activePOI.type)}
                <div className='flex flex-col pl5'>
                    <p className='type-style-5'>
                        {activePOI.label}
                    </p>
                    <p
                        className='type-style-5 black'
                        style={{opacity: 0.5}}>
                        {getDistance(activePOI.coordinates.latitude, activePOI.coordinates.longitude)}
                    </p>

                </div>
            </div>
        );
    };

    useEffect(() => {
        if (activeItem && !isFromList) {
            const section = document.getElementById(activeItem);
            section.scrollIntoView( { behavior: 'smooth', block: 'center' } );
        }

        setIsFromList(false);
    }, [activeItem]);

    return (
        <div className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-grey z3 flex flex-wrap'}>
            <div
                className='md-hide absolute top-0 right right-0 z5 mt5 mr5'>
                <Button
                    aria-label='Close'
                    as = 'button'
                    className = 'bg-black white py2 px3 type-style-5 uppercase items-center'
                    look = 'button'
                    onClick={() => {
                        handleModal();
                    }}
                    renderIcon = {<CloseIcon className='ml3'/>}>
                    Close
                </Button>
            </div>
            <div className='col-12 md-col-8'>
                <Map
                    activeItem={activeItem}
                    center={mapCenter}
                    lat={mapLatitude}
                    lng={mapLongitude}
                    locations={locations}
                    setActiveItem={setActiveItem}
                    setMap={setMap}>
                    <MapCanvas className='map-canvas'/>
                </Map>
            </div>

            {/* Mobile */}
            <div className='sm-hide absolute bottom-0 bg-white p6 width-100'>
                <div className='flex flex-row'>
                    {renderActiveItem()}
                </div>
            </div>

            {/* Tablet / Desktop */}
            <div className='hide sm-show map-info col-12 md-col-4 bg-white height-100'>
                <div className='flex flex-wrap md-flex-no-wrap md-flex-col height-100'>
                    {/* Modal controls */}
                    <div className='border-bottom grey hide md-show'>
                        <div className='md-flex justify-end'>
                            <Button
                                aria-label='Close'
                                as = 'button'
                                className = 'bg-white py2 px3 md-px5 md-py4 md-my3 md-mr3 items-center type-style-5 uppercase'
                                look = 'button'
                                onClick={() => {
                                    handleModal();
                                }}
                                renderIcon = {<CloseIcon className='ml3'/>}>
                                Close
                            </Button>
                        </div>
                    </div>

                    <div className='col-12 sm-col-6 md-col-12 border-right grey md-border-0'>
                        {/* Listing */}
                        <div
                            className='pl5 md-pl7 py6 flex flex-row border-top border-bottom grey cursor-pointer'
                            id={id}
                            onClick={() => {
                                setActiveItem(id);
                                setMapCenter({lat: mapLatitude, lng: mapLongitude });
                            }}>
                            <LocationPinIcon className='black flex-shrink-0'/>
                            <div className='flex flex-col pl5 black pr9'>
                                <p className='type-style-5'>
                                    {price}
                                </p>
                                <h4 className='type-style-4'>
                                    {heading}
                                </h4>
                                <div className='mt4'>
                                    <p className='type-style-5'>
                                        {propertyName}
                                    </p>
                                    <p className='type-style-5'>
                                        {area}{' '}{outwardCode}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Tube */}
                        {poiTube.length > 0 && (
                            <div className='py5 flex flex-wrap border-top md-border-0 md-border-bottom md-border-top grey'>
                                {poiTube.map((tube)=> {
                                    return(
                                        <div
                                            className={`px5 md-px7 py3 flex flex-row black width-100 justify-between items-center md-hover-bg-yellow cursor-pointer${activeItem === tube.id ? ' bg-yellow' : ''}`}
                                            id={tube.id}
                                            key={tube.id}
                                            onClick={() => {
                                                setIsFromList(true);
                                                setActiveItem(tube.id);
                                                setMapCenter({lat: tube.coordinates.latitude, lng: tube.coordinates.longitude });
                                            }}>
                                            <div className='flex items-center'>
                                                <TubeIcon className='flex-shrink-0'/>
                                                <p className='type-style-5 pl5'>
                                                    {tube.label}
                                                </p>
                                            </div>
                                            <p
                                                className='hide md-show type-style-5 black'
                                                style={{opacity: 0.5}}>
                                                {getDistance(tube.coordinates.latitude, tube.coordinates.longitude)}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Amenities */}
                    {poiAmenities.length > 0 &&  (
                        <div className='py5 col-6 md-col-12 md-flex flex-wrap border-top grey border-left md-border-0 md-border-top md-border-bottom'>
                            {poiAmenities.map((amenity)=> {
                                return(
                                    <div
                                        className={`pl7 pr5 md-pr7 md-pl7 py3 flex flex-row black width-100 justify-between md-hover-bg-yellow cursor-pointer${activeItem === amenity.id ? ' bg-yellow' : ''}`}
                                        id={amenity.id}
                                        key={amenity.id}
                                        onClick={() => {
                                            setIsFromList(true);
                                            setActiveItem(amenity.id);
                                            setMapCenter({lat: amenity.coordinates.latitude, lng: amenity.coordinates.longitude});
                                        }}>
                                        <div className='flex items-center'>
                                            {renderIcon(amenity.type)}
                                            <p className='type-style-5 pl5'>
                                                {amenity.label}
                                            </p>
                                        </div>
                                        <p
                                            className='hide md-show type-style-5 black'
                                            style={{opacity: 0.5}}>
                                            {getDistance(amenity.coordinates.latitude, amenity.coordinates.longitude)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default MapModal ;
